/**
 * =============================================================================
 * setera-web
 * =============================================================================
 * Copyright (c) 2026 HG Digital/Setera
 * Todos os direitos reservados.
 *
 * Este software é confidencial e de propriedade da HG Digital/Setera.
 * Seu uso está sujeito aos termos do contrato de licença.
 * -----
 * Criado em : 18-08-2026 12:10:00
 * Autor     : Diego Brock
 * Projeto   : setera-web
 * Versão    : 1.0.0
 * =============================================================================
 */

import { LinkModel } from './hateoas/link-model';

/**
 * Item do histórico de command-executions (tracking-hardware).
 * Paridade com setera-admin-web.
 */
export interface CommandExecutionHistoryItem {
  uuid?: string;
  messageTypeId?: number;
  commandName?: string;
  requestDate?: string;
  status?: string;
  parameters?: string;
  terminalId?: number;
  companyName?: string;
  commandSent?: string;
  response?: string;
  errorMessage?: string | null;
  result?: string | null;
  sentDate?: string | null;
  completedDate?: string | null;
  requiresReauth?: boolean;
  links?: LinkModel[];
  _links?: LinkModel[] | Record<string, LinkModel>;
}

/** Informações de paginação da listagem command-executions. */
export interface CommandExecutionPageInfo {
  size: number;
  totalElements: number;
  totalPages: number;
  number: number;
}

/**
 * Extrai metadados de paginação da resposta HAL/Spring.
 *
 * @param payload resposta bruta da API
 */
export function extractExecutionPageInfo(payload: any): CommandExecutionPageInfo {
  const rawPage = payload && payload.page;
  const page = rawPage != null && typeof rawPage === 'object' ? rawPage : {};
  const size = page.size != null ? page.size : (page.pageSize != null ? page.pageSize : 10);
  const totalElements = page.totalElements != null
    ? page.totalElements
    : (page.total_elements != null ? page.total_elements : 0);
  const totalPages = page.totalPages != null
    ? page.totalPages
    : (page.total_pages != null ? page.total_pages : 0);
  const number = page.number != null ? page.number : 0;
  return { size, totalElements, totalPages, number };
}

/**
 * Indica se a resposta paginada expõe um link HAL (first/next/prev/last).
 *
 * @param pageResource resposta bruta da listagem
 * @param rel nome do link de paginação
 */
export function hasExecutionPaginationRel(pageResource: any, rel: string): boolean {
  if (!pageResource) {
    return false;
  }
  const links = pageResource._links || pageResource.links;
  if (Array.isArray(links)) {
    return links.some((entry) => entry && (entry.rel === rel || entry.name === rel));
  }
  if (links && typeof links === 'object') {
    return rel in links;
  }
  return false;
}
export const COMMAND_EXECUTION_STATUS: { [key: string]: string } = {
  REQUESTED_BY_USER: 'Solicitado pelo usuário',
  SENT_TO_COMMAND_QUEUE: 'Enviado para a fila de comandos',
  SENT_TO_TERMINAL: 'Enviado para o terminal',
  AWAITING_INFO: 'Aguardando informações',
  COMPLETED: 'Completado com sucesso',
  FAILED: 'Falhou na execução',
  TIMEOUT: 'Timeout na execução',
  CANCELLED: 'Cancelado pelo usuário',
};

/**
 * Traduz o código de status da API para o texto exibido.
 *
 * @param status código retornado em command-executions
 * @returns rótulo em português ou o próprio código
 */
export function commandExecutionStatusLabel(status: string): string {
  if (!status) {
    return '';
  }
  return COMMAND_EXECUTION_STATUS[status] || status;
}

/**
 * Extrai links HAL do item (array ou mapa `_links`).
 *
 * @param item execução retornada pela API
 */
export function getExecutionItemLinks(item: CommandExecutionHistoryItem): LinkModel[] {
  if (!item) {
    return [];
  }
  if (Array.isArray(item.links)) {
    return item.links;
  }
  if (Array.isArray(item._links)) {
    return item._links;
  }
  if (item._links && typeof item._links === 'object') {
    const result: LinkModel[] = [];
    Object.keys(item._links).forEach((key) => {
      const value = (item._links as Record<string, LinkModel | LinkModel[]>)[key];
      if (Array.isArray(value)) {
        value.forEach((entry) => {
          if (entry) {
            result.push({
              ...entry,
              rel: entry.rel || key,
              name: entry.name || key,
            });
          }
        });
        return;
      }
      if (value) {
        result.push({
          ...value,
          rel: value.rel || key,
          name: value.name || key,
        });
      }
    });
    return result;
  }
  return [];
}

/**
 * Indica se o backend expôs a ação de cancelamento no item.
 */
export function hasCancelLink(item: CommandExecutionHistoryItem): boolean {
  return getExecutionItemLinks(item).some(
    (link) => link.name === 'cancel' || link.rel === 'cancel'
  );
}

/**
 * Indica se o backend expôs a ação de reenvio no item.
 */
export function hasResendLink(item: CommandExecutionHistoryItem): boolean {
  return getExecutionItemLinks(item).some(
    (link) => link.name === 'resend' || link.rel === 'resend'
  );
}

/**
 * Indica se a execução exige reautenticação antes de reenviar/cancelar.
 */
export function executionRequiresReauth(item: CommandExecutionHistoryItem): boolean {
  const raw = item as CommandExecutionHistoryItem & { requires_reauth?: boolean };
  return !!(item.requiresReauth || raw.requires_reauth);
}
